import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react';

interface CaptionsContextType {
  showCaptions: boolean;
  toggleCaptions: () => void;
}

// Create context with default values
const CaptionsContext = createContext<CaptionsContextType>({
  showCaptions: true,
  toggleCaptions: () => {},
});

interface CaptionsProviderProps {
  children: ReactNode;
  initialShowCaptions?: boolean;
}

export const CaptionsProvider: React.FC<CaptionsProviderProps> = ({ 
  children,
  initialShowCaptions = true
}) => {
  const [showCaptions, setShowCaptions] = useState(initialShowCaptions);

  const toggleCaptions = () => {
    setShowCaptions(prev => !prev);
  };

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    showCaptions,
    toggleCaptions,
  }), [showCaptions]);

  return (
    <CaptionsContext.Provider value={contextValue}>
      {children}
    </CaptionsContext.Provider>
  );
};

// Custom hook with error handling
export const useCaptions = (): CaptionsContextType => {
  const context = useContext(CaptionsContext);
  
  if (context === undefined) {
    console.warn('useCaptions must be used within a CaptionsProvider');
    // Return default values if used outside provider
    return {
      showCaptions: true,
      toggleCaptions: () => {},
    };
  }
  
  return context;
};
